/* ==========================================================================
   Temel – theme.js
   Framework yok; her etkileşim bir Web Component. Tema editöründe section
   yeniden yüklendiğinde custom element'ler kendiliğinden yeniden bağlanır.
   ========================================================================== */

const theme = window.theme || {};

/* ---------- Yardımcılar ---------- */
function showToast(message, link) {
  const toast = document.querySelector('[data-toast]');
  if (!toast) return;
  toast.textContent = message;
  if (link) {
    const a = document.createElement('a');
    a.href = link.href;
    a.textContent = link.label;
    toast.append(a);
  }
  toast.hidden = false;
  clearTimeout(showToast.timer);
  showToast.timer = setTimeout(() => (toast.hidden = true), 3500);
}

async function updateCartCount() {
  try {
    const res = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
    const cart = await res.json();
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
  } catch (e) {
    /* sessizce geç */
  }
}

/* Header yüksekliğini CSS değişkeni olarak yayınla (mobil çekmece & sticky bilgi alanı için) */
function setHeaderHeight() {
  const header = document.querySelector('.header');
  if (!header) return;
  const rect = header.getBoundingClientRect();
  document.documentElement.style.setProperty('--header-height', `${rect.height}px`);
  document.documentElement.style.setProperty('--header-bottom', `${Math.max(rect.bottom, 0)}px`);
}
window.addEventListener('resize', setHeaderHeight, { passive: true });
document.addEventListener('DOMContentLoaded', setHeaderHeight);
document.addEventListener('shopify:section:load', setHeaderHeight);

/* ---------- Açılır menüler: dışarı tıklayınca / ESC ile kapat ---------- */
document.addEventListener('click', (event) => {
  document.querySelectorAll('.header__dropdown[open], .facets__filter[open], .header__drawer[open]').forEach((details) => {
    if (!details.contains(event.target)) details.removeAttribute('open');
  });
});
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('details[open]:not(.disclosure)').forEach((details) => {
    details.removeAttribute('open');
    details.querySelector('summary')?.focus();
  });
});
document.addEventListener(
  'toggle',
  (event) => {
    if (!event.target.matches?.('.header__drawer')) return;
    setHeaderHeight();
    document.body.style.overflow = event.target.open ? 'hidden' : '';
  },
  true
);

/* ---------- Otomatik gönderilen formlar (filtreler, sepet adedi) ---------- */
document.addEventListener('change', (event) => {
  const form = event.target.closest('form[data-auto-submit]');
  if (form) {
    // Boş fiyat alanlarını URL'ye yazma
    form.querySelectorAll('input[type="number"]').forEach((input) => {
      input.disabled = input.value === '';
    });
    form.submit();
    return;
  }
  if (event.target.matches('[data-auto-submit-input]')) {
    event.target.form?.submit();
  }
});

/* ---------- <variant-picker> ---------- */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.abortController?.abort();
  }

  onChange = async (event) => {
    const input = event.target.tagName === 'SELECT' ? event.target.selectedOptions[0] : event.target;
    const ids = [...this.querySelectorAll('input:checked, select')]
      .map((el) => (el.tagName === 'SELECT' ? el.selectedOptions[0] : el).dataset.optionValueId)
      .join(',');
    const targetUrl = input.dataset.productUrl || this.dataset.productUrl;

    // Combined listing: farklı ürüne geçiş -> tam sayfa
    if (targetUrl !== this.dataset.productUrl) {
      window.location.href = `${targetUrl}?option_values=${ids}`;
      return;
    }

    const sectionId = this.dataset.sectionId;
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.classList.add('is-loading');

    try {
      const res = await fetch(`${targetUrl}?option_values=${ids}&section_id=${sectionId}`, {
        signal: this.abortController.signal,
      });
      const html = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = html.getElementById(`shopify-section-${sectionId}`);
      const current = document.getElementById(`shopify-section-${sectionId}`);
      if (!fresh || !current) return;

      const focusedValueId = input.dataset.optionValueId;
      current.innerHTML = fresh.innerHTML;

      const variantId = current.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
      if (variantId) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        url.searchParams.delete('option_values');
        window.history.replaceState({}, '', url);
      }
      const focusTarget = current.querySelector(`[data-option-value-id="${focusedValueId}"]`);
      (focusTarget?.tagName === 'OPTION' ? focusTarget.parentElement : focusTarget)?.focus();
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      this.classList.remove('is-loading');
    }
  };
}
customElements.define('variant-picker', VariantPicker);

/* ---------- <product-form> : AJAX sepete ekleme ---------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
  }

  onSubmit = async (event) => {
    if (theme.cartType === 'page') return; // normal form gönderimi -> /cart
    event.preventDefault();

    const buttons = document.querySelectorAll(`[type="submit"][name="add"][form="${this.form.id}"]`);
    const button = this.form.querySelector('[type="submit"]');
    const error = this.form.querySelector('[data-form-error]');
    [button, ...buttons].forEach((b) => b?.classList.add('is-loading'));
    if (error) error.hidden = true;

    const drawer = document.querySelector('cart-drawer');
    const body = new FormData(this.form);
    if (drawer) {
      body.append('sections', 'cart-drawer');
      body.append('sections_url', window.location.pathname);
    }

    try {
      const res = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        const message = data.description || data.message || theme.strings.error;
        if (error) {
          error.textContent = message;
          error.hidden = false;
        } else {
          showToast(message);
        }
        return;
      }
      await updateCartCount();
      if (drawer && data.sections?.['cart-drawer']) {
        drawer.renderSection(data.sections['cart-drawer']);
        drawer.open(button);
      } else {
        showToast(theme.strings.added, { href: theme.routes.cart, label: theme.strings.viewCart });
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    } catch (e) {
      showToast(theme.strings.error);
    } finally {
      [button, ...buttons].forEach((b) => b?.classList.remove('is-loading'));
    }
  };
}
customElements.define('product-form', ProductForm);

/* ---------- <cart-drawer> ---------- */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('keydown', (e) => e.key === 'Escape' && this.close());
    document.addEventListener('click', this.onOpenClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOpenClick);
  }

  onOpenClick = (event) => {
    const trigger = event.target.closest('[data-cart-open]');
    if (!trigger) return;
    event.preventDefault();
    this.open(trigger);
  };

  open(trigger) {
    this.trigger = trigger;
    this.classList.add('is-open');
    document.body.classList.add('has-drawer-open');
    requestAnimationFrame(() => this.querySelector('.cart-drawer__panel')?.focus());
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('has-drawer-open');
    this.trigger?.focus?.();
  }

  renderSection(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('cart-drawer');
    if (fresh) this.innerHTML = fresh.innerHTML;
  }

  onClick = (event) => {
    if (event.target.closest('[data-cart-close]')) return this.close();
    const qtyButton = event.target.closest('[data-cart-qty]');
    if (qtyButton) {
      const line = qtyButton.closest('[data-line]').dataset.line;
      this.changeLine(line, Number(qtyButton.dataset.cartQty));
    }
  };

  onChange = (event) => {
    if (!event.target.matches('[data-cart-note]')) return;
    fetch(`${theme.routes.cart}/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: event.target.value }),
    });
  };

  async changeLine(line, quantity) {
    this.classList.add('is-loading');
    try {
      const res = await fetch(`${theme.routes.cart}/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity, sections: ['cart-drawer'], sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (data.status) {
        showToast(data.description || theme.strings.error);
        return;
      }
      if (data.sections?.['cart-drawer']) this.renderSection(data.sections['cart-drawer']);
      await updateCartCount();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: this } }));
    } finally {
      this.classList.remove('is-loading');
    }
  }
}
customElements.define('cart-drawer', CartDrawer);

/* ---------- <cart-page> : sepet sayfasında yenilemeden güncelleme ---------- */
class CartPage extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    document.addEventListener('cart:updated', this.onCartUpdated);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.onCartUpdated);
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  onChange = (event) => {
    const input = event.target;
    if (input.matches('[data-cart-line-qty]')) {
      clearTimeout(this.qtyTimer);
      this.qtyTimer = setTimeout(() => this.changeLine(input.closest('[data-line]').dataset.line, Number(input.value)), 300);
    } else if (input.matches('[data-cart-page-note]')) {
      fetch(`${theme.routes.cart}/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: input.value }),
      });
    }
  };

  onClick = (event) => {
    const remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;
    event.preventDefault();
    const line = remove.closest('[data-line]');
    line.classList.add('is-removing');
    this.changeLine(line.dataset.line, 0);
  };

  // Başka yerden (çekmece, hızlı ekleme) sepet değişirse sayfayı tazele
  onCartUpdated = async (event) => {
    if (event.detail?.source === this) return;
    const res = await fetch(`${theme.routes.cart}?section_id=${this.sectionId}`);
    if (res.ok) this.render(await res.text());
  };

  render(html) {
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-page');
    if (!fresh) return;
    this.innerHTML = fresh.innerHTML;
    this.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
  }

  async changeLine(line, quantity) {
    this.classList.add('is-loading');
    try {
      const res = await fetch(`${theme.routes.cart}/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity, sections: [this.sectionId, 'cart-drawer'], sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (data.status) {
        showToast(data.description || theme.strings.error);
        this.onCartUpdated({});
        return;
      }
      if (data.sections?.[this.sectionId]) this.render(data.sections[this.sectionId]);
      if (data.sections?.['cart-drawer']) document.querySelector('cart-drawer')?.renderSection(data.sections['cart-drawer']);
      await updateCartCount();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: this } }));
    } finally {
      this.classList.remove('is-loading');
    }
  }
}
customElements.define('cart-page', CartPage);

/* ---------- <quantity-input> ---------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.querySelectorAll('[data-step]').forEach((button) =>
      button.addEventListener('click', () => {
        const step = Number(this.input.step) || 1;
        const min = Number(this.input.min) || 1;
        const max = this.input.max ? Number(this.input.max) : Infinity;
        const next = Number(this.input.value) + step * Number(button.dataset.step);
        this.input.value = Math.min(max, Math.max(min, next));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      })
    );
  }
}
customElements.define('quantity-input', QuantityInput);

/* ---------- <sticky-atc> : mobilde ana buton görünmezken çıkar ---------- */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    const main = document.querySelector('[data-main-atc]');
    if (!main) return;
    this.hidden = false;
    this.observer = new IntersectionObserver(([entry]) => {
      const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.classList.toggle('is-visible', passed);
    });
    this.observer.observe(main);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}
customElements.define('sticky-atc', StickyAtc);

/* ---------- <delivery-countdown> : İstanbul saatine göre ---------- */
class DeliveryCountdown extends HTMLElement {
  connectedCallback() {
    this.text = this.querySelector('[data-delivery-text]');
    this.update();
    this.timer = setInterval(() => this.update(), 30000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  update() {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Istanbul', weekday: 'short', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' })
        .formatToParts(new Date())
        .map((p) => [p.type, p.value])
    );
    const day = parts.weekday; // Mon..Sun
    const minutesNow = Number(parts.hour) * 60 + Number(parts.minute);
    const cutoff = Number(this.dataset.cutoff) * 60;
    const shipsSaturday = this.dataset.saturday === 'true';
    const workday = !['Sun'].includes(day) && (day !== 'Sat' || shipsSaturday);

    if (workday && minutesNow < cutoff) {
      const left = cutoff - minutesNow;
      const h = Math.floor(left / 60);
      const m = left % 60;
      const time = h > 0 ? `${h} sa ${m} dk` : `${m} dk`;
      this.text.innerHTML = this.dataset.textToday.replace('{time}', `<span class="delivery__time">${time}</span>`);
      return;
    }
    // Bir sonraki kargo günü yarın mı, yoksa pazartesi mi?
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const tomorrow = days[(days.indexOf(day) + 1) % 7];
    const tomorrowShips = tomorrow !== 'Sun' && (tomorrow !== 'Sat' || shipsSaturday);
    this.text.innerHTML = tomorrowShips ? this.dataset.textTomorrow : this.dataset.textMonday;
  }
}
customElements.define('delivery-countdown', DeliveryCountdown);

/* ---------- <product-recommendations> ---------- */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.querySelector('.product-grid')) return;
    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        const res = await fetch(this.dataset.url);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('product-recommendations');
        if (fresh?.innerHTML.trim()) {
          this.innerHTML = fresh.innerHTML;
          initReveal(this);
        }
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    observer.observe(this);
  }
}
customElements.define('product-recommendations', ProductRecommendations);

/* ---------- Şeffaf header: kaydırınca dolu ---------- */
let headerTicking = false;
function onScrollHeader() {
  if (headerTicking) return;
  headerTicking = true;
  requestAnimationFrame(() => {
    const header = document.querySelector('.header--transparent');
    if (header) header.classList.toggle('is-scrolled', window.scrollY > 40);
    headerTicking = false;
  });
}
window.addEventListener('scroll', onScrollHeader, { passive: true });
document.addEventListener('DOMContentLoaded', onScrollHeader);
document.addEventListener('shopify:section:load', onScrollHeader);

/* ---------- Kaydırınca beliren içerik ---------- */
function initReveal(root = document) {
  if (!document.body.classList.contains('has-reveal')) return;
  const items = root.querySelectorAll('[data-reveal]:not(.is-revealed)');
  if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-revealed'));
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px' }
  );
  items.forEach((el) => {
    const siblings = [...el.parentElement.parentElement.querySelectorAll(':scope > li > [data-reveal]')];
    const index = siblings.indexOf(el);
    if (index > 0) el.style.setProperty('--reveal-delay', `${Math.min(index, 6) * 0.08}s`);
    observer.observe(el);
  });
}
document.addEventListener('DOMContentLoaded', () => initReveal());
document.addEventListener('shopify:section:load', (e) => initReveal(e.target));

/* ---------- <announcement-bar> : mesajlar sırayla döner ---------- */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.announcement-bar__item')];
    if (this.items.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.index = 0;
    this.timer = setInterval(() => this.next(), Number(this.dataset.interval) || 5000);
    this.addEventListener('mouseenter', () => clearInterval(this.timer));
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  next() {
    this.items[this.index].classList.remove('is-active');
    this.index = (this.index + 1) % this.items.length;
    this.items[this.index].classList.add('is-active');
  }
}
customElements.define('announcement-bar', AnnouncementBar);


/* ---------- Yumuşak kaydırma (masaüstü, fare/trackpad) ----------
   Tekerlek olayını yakalayıp hedef konuma ataletle ilerler.
   Kendi içinde kayan alanlar, modal açıkken, ctrl+tekerlek (zoom),
   tema editörü, dokunmatik ve "hareketi azalt" durumlarında devreye girmez. */
class SmoothScroll {
  constructor({ ease = 0.1 } = {}) {
    this.ease = ease;
    this.current = window.scrollY;
    this.target = window.scrollY;
    this.running = false;
    window.addEventListener('wheel', this.onWheel, { passive: false });
    window.addEventListener('scroll', this.onNativeScroll, { passive: true });
    window.addEventListener('resize', () => (this.target = this.clamp(this.target)), { passive: true });
  }

  get max() {
    return document.documentElement.scrollHeight - window.innerHeight;
  }

  clamp(value) {
    return Math.max(0, Math.min(value, this.max));
  }

  // Tıklanan yerin üstünde, bu yönde kayabilen bir kutu varsa ona dokunma
  hasScrollableParent(node, deltaY) {
    for (let el = node; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
      const style = getComputedStyle(el);
      if (!/(auto|scroll)/.test(style.overflowY) || el.scrollHeight <= el.clientHeight) continue;
      if (deltaY < 0 && el.scrollTop > 0) return true;
      if (deltaY > 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 1) return true;
    }
    return false;
  }

  onWheel = (event) => {
    if (event.defaultPrevented || event.ctrlKey || event.metaKey) return;
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return; // yatay trackpad hareketi
    if (document.body.classList.contains('has-drawer-open')) return;
    if (event.target.closest?.('select, textarea, [data-native-scroll], details[open] .facets__panel')) return;
    if (this.hasScrollableParent(event.target, event.deltaY)) return;

    event.preventDefault();
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
    if (!this.running) this.current = this.target = window.scrollY;
    this.target = this.clamp(this.target + event.deltaY * unit);
    if (!this.running) {
      this.running = true;
      requestAnimationFrame(this.tick);
    }
  };

  // Klavye, kaydırma çubuğu, anchor link gibi yerel kaydırmalarla senkron kal
  onNativeScroll = () => {
    if (!this.running) this.current = this.target = window.scrollY;
  };

  tick = () => {
    this.current += (this.target - this.current) * this.ease;
    if (Math.abs(this.target - this.current) < 0.5) {
      this.current = this.target;
      this.running = false;
    }
    window.scrollTo(0, this.current);
    if (this.running) requestAnimationFrame(this.tick);
  };
}

(function initSmoothScroll() {
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!theme.smoothScroll || !fine || reduce || window.Shopify?.designMode) return;
  document.documentElement.classList.add('has-smooth-scroll');
  window.smoothScroll = new SmoothScroll();
})();

/* ---------- <store-locator> : şehir/tür filtresi, arama, harita ---------- */
class StoreLocator extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-stockist]')];
    this.search = this.querySelector('[data-locator-search]');
    this.count = this.querySelector('[data-locator-count]');
    this.empty = this.querySelector('[data-locator-empty]');
    this.map = this.querySelector('[data-locator-map]');
    this.mapLabel = this.querySelector('[data-locator-map-label]');
    this.city = '';
    this.type = '';
    this.buildFilters();

    this.querySelectorAll('[data-city-filter], [data-type-filter]').forEach((button) =>
      button.addEventListener('click', () => {
        this.city = button.dataset.cityFilter ?? '';
        this.type = button.dataset.typeFilter ?? '';
        this.querySelectorAll('[data-city-filter], [data-type-filter]').forEach((b) => {
          const active = b === button;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', active);
        });
        this.apply();
      })
    );
    this.search?.addEventListener('input', () => this.apply());
    this.addEventListener('click', (event) => {
      const select = event.target.closest('[data-stockist-select]');
      if (select) this.focusOnMap(select.closest('[data-stockist]'));
    });
    this.apply();
  }

  // Şehir filtreleri mağaza kartlarından otomatik oluşur (editörde şehir değişince kendiliğinden güncellenir)
  buildFilters() {
    const wrap = this.querySelector('[data-locator-filters]');
    if (!wrap) return;
    const counts = {};
    this.items.forEach((i) => i.dataset.city && (counts[i.dataset.city] = (counts[i.dataset.city] || 0) + 1));
    const cities = Object.keys(counts).sort((a, b) => a.localeCompare(b, 'tr'));
    const make = (label, attrs) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'pill';
      b.setAttribute('aria-pressed', 'false');
      Object.entries(attrs).forEach(([k, v]) => (b.dataset[k] = v));
      b.textContent = label;
      wrap.append(b);
    };
    cities.forEach((city) => make(`${city} (${counts[city]})`, { cityFilter: city }));
    if (this.items.some((i) => i.dataset.type === 'online')) make(wrap.dataset.labelOnline, { typeFilter: 'online' });
  }

  normalize(value) {
    return (value || '').toLocaleLowerCase('tr').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ı/g, 'i');
  }

  apply() {
    const term = this.normalize(this.search?.value.trim());
    let visible = 0;
    this.items.forEach((item) => {
      const matchCity = !this.city || item.dataset.city === this.city;
      const matchType = !this.type || item.dataset.type === this.type;
      const matchTerm = !term || this.normalize(item.dataset.search).includes(term);
      const show = matchCity && matchType && matchTerm;
      item.hidden = !show;
      if (show) visible++;
    });
    if (this.count) this.count.textContent = `${visible} satış noktası`;
    if (this.empty) this.empty.hidden = visible > 0;
  }

  focusOnMap(item) {
    if (!item || !this.map || item.dataset.type === 'online') return;
    this.items.forEach((i) => i.classList.toggle('is-selected', i === item));
    this.map.src = `https://www.google.com/maps?q=${encodeURIComponent(item.dataset.map)}&output=embed`;
    if (this.mapLabel) this.mapLabel.textContent = item.querySelector('.stockist__name')?.textContent || '';
    if (window.matchMedia('(max-width: 989px)').matches) this.map.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}
customElements.define('store-locator', StoreLocator);

/* ---------- Header açılır menü: masaüstünde üzerine gelince aç ---------- */
(function initHoverMenus() {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  document.querySelectorAll('.header__dropdown').forEach((details) => {
    let timer;
    details.addEventListener('mouseenter', () => {
      clearTimeout(timer);
      document.querySelectorAll('.header__dropdown[open]').forEach((d) => d !== details && d.removeAttribute('open'));
      details.setAttribute('open', '');
    });
    details.addEventListener('mouseleave', () => {
      timer = setTimeout(() => details.removeAttribute('open'), 180);
    });
  });
})();
